<template>
    <div
        id="main-layout"
        class="main-layout"
        :class="{
            'sidebar-hidden': !sidebarExpanded,
            'sidebar-non-sticky': !sidebarSticky,
            'dark-theme': isDarkTheme,
            'light-theme': !isDarkTheme,
        }"
    >
        <a href="#" @click="skipMainContent" class="skip-main-content">
            <span>{{ $t("Skip to main content") }}</span>
        </a>
        <TopBar @toggle-menu="toggleMenu" @openModal="openModal"></TopBar>
        <AuthLoadingOverlay :display="loadingAuth"></AuthLoadingOverlay>
        <router-view v-if="!loadingAuth"></router-view>
        <LoadingOverlay v-if="loadingRoute"></LoadingOverlay>

        <AccountSettingsDropdown
            v-if="displayDropdownAccountSettings"
            v-model:display="displayDropdownAccountSettings"
            @openModal="openModal"
        ></AccountSettingsDropdown>

        <GeneralConfigurationModal v-if="displayModalSetting" v-model:display="displayModalSetting"></GeneralConfigurationModal>

        <ChangeLanguageModal v-if="displayModalLanguage" v-model:display="displayModalLanguage"></ChangeLanguageModal>

        <ProfileModal v-if="displayProfileModal" v-model:display="displayProfileModal" :uid="profileModalUser"> </ProfileModal>

        <LogoutModal v-if="displayModalLogout" v-model:display="displayModalLogout"></LogoutModal>

        <MessageModal
            v-if="displayModalMessage"
            v-model:display="displayModalMessage"
            :title="messageModalTitle"
            :message="messageModalMessage"
        ></MessageModal>

        <MessageIconModal
            v-if="displayIconModalMessage"
            v-model:display="displayIconModalMessage"
            :title="messageIconModalTitle"
            :message="messageIconModalMessage"
            :icon="messageIconModalIcon"
        >
        </MessageIconModal>

        <ConfirmationModal
            v-if="displayConfirmationModal"
            v-model:display="displayConfirmationModal"
            :title="confirmationTitle"
            :message="confirmationMessage"
            :danger="confirmationDanger"
            :simple-delete="confirmationSimpleDelete"
            :show-again="confirmationShowAgain"
            :icon="confirmationIcon"
            @confirm="onConfirm"
        >
        </ConfirmationModal>

        <ImageSelectorModal
            v-if="displayImageSelectorModal"
            v-model:display="displayImageSelectorModal"
            :file="selectorImageFile"
            @confirm="onConfirmImageSelectorModal"
        >
        </ImageSelectorModal>

        <CookiesModal v-if="displayModalCookies" v-model:display="displayModalCookies"></CookiesModal>

        <SnackBar></SnackBar>
        <NotificationModal></NotificationModal>
        <ChatBotWidget v-if="isAuthenticated && !loadingAuth && !loadingRoute"></ChatBotWidget>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import LoadingOverlay from "@/components/layout/LoadingOverlay.vue";
import AuthLoadingOverlay from "@/components/layout/AuthLoadingOverlay.vue";
import SnackBar from "@/components/layout/SnackBar.vue";

import LogoutModal from "@/components/modals/auth/LogoutModal.vue";

import ChangeLanguageModal from "@/components/modals/ChangeLanguageModal.vue";

import ProfileModal from "@/components/modals/ProfileModal.vue";

import MessageModal from "@/components/modals/MessageModal.vue";
import MessageIconModal from "@/components/modals/MessageIconModal.vue";

import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import ImageSelectorModal from "@/components/modals/ImageSelectorModal.vue";

import CookiesModal from "@/components/modals/CookiesModal.vue";

import { AuthController } from "@/control/auth";

import { fetchFromLocalStorageCache, saveIntoLocalStorage } from "@/utils/local-storage";
import NotificationModal from "@/components/modals/NotificationModal.vue";
import AccountSettingsDropdown from "@/components/modals/auth/AccountSettingsDropdown.vue";
import GeneralConfigurationModal from "@/components/modals/GeneralConfigurationModal.vue";
import { getCookiePreference, getTheme } from "@/control/app-preferences";
import TopBar from "@/components/layout/TopBar.vue";
import ChatBotWidget from "@/components/layout/ChatBotWidget.vue";

export default defineComponent({
    components: {
        TopBar,
        ChatBotWidget,
        LoadingOverlay,
        AuthLoadingOverlay,
        SnackBar,
        LogoutModal,
        MessageModal,
        MessageIconModal,
        ChangeLanguageModal,
        ConfirmationModal,
        ProfileModal,
        CookiesModal,
        ImageSelectorModal,
        NotificationModal,
        AccountSettingsDropdown,
        GeneralConfigurationModal,
    },
    name: "MainLayout",
    setup: function () {
        return {
            confirmationCallback: null,
            imageSelectorCallback: null,
            requestCallCodeCallback: null,
            resizeObserver: null as ResizeObserver,
        };
    },
    data: function () {
        return {
            loadingRoute: false,

            sidebarExpanded: fetchFromLocalStorageCache("sidebar-expanded", true),
            sidebarSticky: true,

            backgroundImage: "",

            isDarkTheme: false,
            loadingAuth: AuthController.Loading && !AuthController.FirstTimeLoaded,
            isAuthenticated: AuthController.isAuthenticated(),

            displayModalLogout: false,

            displayModalLanguage: false,

            displayModalMessage: false,
            messageModalTitle: "",
            messageModalMessage: "",

            displayIconModalMessage: false,
            messageIconModalTitle: "",
            messageIconModalMessage: "",
            messageIconModalIcon: "",

            displayConfirmationModal: false,

            selectorImageFile: null,
            displayImageSelectorModal: false,

            confirmationTitle: "",
            confirmationMessage: "",
            confirmationDanger: false,
            confirmationSimpleDelete: false,
            confirmationShowAgain: false,
            confirmationIcon: "",

            displayProfileModal: false,
            profileModalUser: "",

            displayModalCookies: false,

            displayDropdownAccountSettings: false,
            displayModalSetting: false,
        };
    },
    methods: {
        onAuthStatusUpdate: function () {
            this.displayModalLogout = false;
            this.displayModalLanguage = false;
            this.isAuthenticated = AuthController.isAuthenticated();
        },

        closeSideBar: function () {
            this.sidebarExpanded = false;
            saveIntoLocalStorage("sidebar-expanded", this.sidebarExpanded);
        },

        onAuthLoadingChanged: function () {
            this.loadingAuth = AuthController.Loading && !AuthController.FirstTimeLoaded;
        },

        onThemeChanged: function () {
            this.isDarkTheme = getTheme() === "dark";
        },

        toggleMenu: function () {
            this.sidebarExpanded = !this.sidebarExpanded;
            saveIntoLocalStorage("sidebar-expanded", this.sidebarExpanded);
        },

        skipMainContent: function (event) {
            if (event) {
                event.preventDefault();
            }
            const content: any = document.querySelector(".page-content");
            if (content) {
                content.focus();
            }
        },

        showMessage: function (title: string, msg: string) {
            this.messageModalTitle = title;
            this.messageModalMessage = msg;
            this.displayModalMessage = true;
        },

        showMessageIcon: function (title: string, msg: string, icon: string) {
            this.messageIconModalTitle = title;
            this.messageIconModalMessage = msg;
            this.messageIconModalIcon = icon;
            this.displayIconModalMessage = true;
        },

        openModal: function (modalName: any) {
            switch (modalName) {
                case "change-language-modal":
                    this.displayModalLanguage = true;
                    break;
                case "logout":
                    this.displayModalLogout = true;
                    break;
                case "account-settings":
                    this.displayDropdownAccountSettings = true;
                    break;
                case "settings":
                    this.displayModalSetting = true;
                    break;
            }
        },

        onRouterLoading: function (l: boolean) {
            this.loadingRoute = l;
        },

        onRouteChanged: function () {
            if (this.$route && this.$route.meta && typeof this.$route.meta === "object") {
                this.sidebarSticky = !!this.$route.meta.sidebarSticky;
            } else {
                this.sidebarExpanded = false;
                this.sidebarSticky = false;
            }

            const cookiePref = getCookiePreference();

            if (!cookiePref && (!this.$route || !(this.$route.name in { terms: 1, privacy: 1, cookies: 1 }))) {
                this.displayModalCookies = true;
            } else {
                this.displayModalCookies = false;
            }
        },

        showConfirmationModal: function (options: {
            title: string;
            message: string;
            danger?: boolean;
            simpleDelete?: boolean;
            showAgain?: boolean;
            icon?: string;
            callback: () => void;
        }) {
            this.displayConfirmationModal = true;
            this.confirmationTitle = options.title;
            this.confirmationMessage = options.message;
            this.confirmationDanger = !!options.danger;
            this.confirmationSimpleDelete = !!options.simpleDelete;
            this.confirmationShowAgain = !!options.simpleDelete;
            this.confirmationIcon = options.icon;
            this.confirmationCallback = options.callback;
        },

        showImageSelectorModal: function (options: { file: File; callback: (file: File) => void }) {
            this.imageSelectorCallback = options.callback;
            this.selectorImageFile = options.file;
            this.displayImageSelectorModal = true;
        },

        onConfirmImageSelectorModal: function (file: File) {
            if (this.imageSelectorCallback) {
                this.imageSelectorCallback(file);
            }
        },

        onConfirm: function () {
            if (this.confirmationCallback) {
                this.confirmationCallback();
            }
        },

        onShowProfile: function (uid: string) {
            this.profileModalUser = uid;
            this.displayProfileModal = true;
        },

        onPageResize: function () {
            this.sidebarExpanded = fetchFromLocalStorageCache("sidebar-expanded", true);
        },

        onNotification: function (notification: any) {
            if (this.$route.name === "organization") {
                switch (notification.data.type) {
                    case "invitation_organization":
                        this.$showSnackBar(this.$t("You have a pending invitation"), "right");
                        break;
                }
                return;
            }
            if (this.$route.name !== "activity") {
                this.$showNotification(notification);
            }
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", this.onAuthStatusUpdate.bind(this));

        this.$listenOnAppEvent("auth-status-loading", this.onAuthLoadingChanged.bind(this));

        this.$listenOnAppEvent("theme-changed", this.onThemeChanged.bind(this));
        this.onThemeChanged();

        this.$listenOnAppEvent("msg-modal", this.showMessage.bind(this));
        this.$listenOnAppEvent("msg-modal-icon", this.showMessageIcon.bind(this));
        this.$listenOnAppEvent("ask-confirmation", this.showConfirmationModal.bind(this));

        this.$listenOnAppEvent("image-selector-modal", this.showImageSelectorModal.bind(this));

        this.$listenOnAppEvent("show-profile", this.onShowProfile.bind(this));

        this.$listenOnAppEvent("router-load-state-change", this.onRouterLoading.bind(this));

        this.$listenOnAppEvent("notification", this.onNotification.bind(this));

        this.onAuthStatusUpdate();

        this.onRouteChanged();

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(this.onPageResize.bind(this));
            this.resizeObserver.observe(this.$el);
        }
    },
    watch: {
        $route: function () {
            this.onRouteChanged();
        },
    },
    beforeUnmount: function () {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
});
</script>

<style></style>
